import { apiClient } from './apiClient';
import type {
  AppraisalCycle,
  CreateAppraisalCycleRequest,
  UpdateAppraisalCycleRequest,
  AppraisalCyclesFilterRequest,
  ImportMappingsResult,
  StartAppraisalProcessResult,
  CycleAppraisal,
  CycleAppraisalsFilterRequest,
  UpdateCycleAppraisalRequest,
  PaginatedResponse,
  BulkActionResult,
} from './types/appraisalCycle.types';

export const appraisalCyclesApi = {
  // Cycles

  getAll: async (
    filter: AppraisalCyclesFilterRequest
  ): Promise<PaginatedResponse<AppraisalCycle>> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(filter.page));
      params.append('pageSize', String(filter.pageSize));
      params.append('sortBy', filter.sortBy);
      params.append('sortDirection', filter.sortDirection);
      if (filter.name) params.append('name', filter.name);
      if (filter.status) params.append('status', filter.status);

      const res = await apiClient.get(`/appraisal-cycles?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch appraisal cycles:', error);
      throw error;
    }
  },

  getActive: async (): Promise<AppraisalCycle | null> => {
    try {
      const res = await apiClient.get('/appraisal-cycles/active');
      return res.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      console.error('Failed to fetch active appraisal cycle:', error);
      throw error;
    }
  },

  create: async (request: CreateAppraisalCycleRequest): Promise<AppraisalCycle> => {
    try {
      const res = await apiClient.post('/appraisal-cycles', request);
      return res.data;
    } catch (error) {
      console.error('Failed to create appraisal cycle:', error);
      throw error;
    }
  },

  update: async (id: number, request: UpdateAppraisalCycleRequest, rowVersion?: string): Promise<AppraisalCycle> => {
    try {
      const res = await apiClient.put(`/appraisal-cycles/${id}`, { ...request, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update appraisal cycle:', error);
      throw error;
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/appraisal-cycles/${id}`);
    } catch (error) {
      console.error('Failed to delete appraisal cycle:', error);
      throw error;
    }
  },

  importMappings: async (cycleId: number, file: File): Promise<ImportMappingsResult> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await apiClient.post(`/appraisal-cycles/${cycleId}/import-mappings`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    } catch (error) {
      console.error('Failed to import mappings:', error);
      throw error;
    }
  },

  startProcess: async (cycleId: number, rowVersion?: string): Promise<StartAppraisalProcessResult> => {
    try {
      const res = await apiClient.post(`/appraisal-cycles/${cycleId}/start`, { rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to start appraisal process:', error);
      throw error;
    }
  },

  getMappingsPreview: async (
    cycleId: number,
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<CycleAppraisal>> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));
      const res = await apiClient.get(`/appraisal-cycles/${cycleId}/mappings-preview?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch mappings preview:', error);
      throw error;
    }
  },

  // Cycle Appraisals

  getAppraisals: async (
    cycleId: number,
    filter: CycleAppraisalsFilterRequest
  ): Promise<PaginatedResponse<CycleAppraisal>> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(filter.page));
      params.append('pageSize', String(filter.pageSize));
      params.append('sortBy', filter.sortBy);
      params.append('sortDirection', filter.sortDirection);
      if (filter.appraiseeName) params.append('appraiseeName', filter.appraiseeName);
      if (filter.collatorName) params.append('collatorName', filter.collatorName);
      if (filter.status) params.append('status', filter.status);
      if (filter.selfAssessmentDateFrom) params.append('selfAssessmentDateFrom', filter.selfAssessmentDateFrom);
      if (filter.selfAssessmentDateTo) params.append('selfAssessmentDateTo', filter.selfAssessmentDateTo);
      if (filter.collationDeadlineFrom) params.append('collationDeadlineFrom', filter.collationDeadlineFrom);
      if (filter.collationDeadlineTo) params.append('collationDeadlineTo', filter.collationDeadlineTo);
      if (filter.earliestDeliveryDateFrom) params.append('earliestDeliveryDateFrom', filter.earliestDeliveryDateFrom);
      if (filter.earliestDeliveryDateTo) params.append('earliestDeliveryDateTo', filter.earliestDeliveryDateTo);
      if (filter.tcDateFrom) params.append('tcDateFrom', filter.tcDateFrom);
      if (filter.tcDateTo) params.append('tcDateTo', filter.tcDateTo);
      if (filter.geo) params.append('geo', filter.geo);
      if (filter.department) params.append('department', filter.department);

      const res = await apiClient.get(`/appraisal-cycles/${cycleId}/appraisals?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch cycle appraisals:', error);
      throw error;
    }
  },

  updateAppraisal: async (
    cycleId: number,
    appraisalId: number,
    request: UpdateCycleAppraisalRequest
  ): Promise<CycleAppraisal> => {
    try {
      const res = await apiClient.put(`/appraisal-cycles/${cycleId}/appraisals/${appraisalId}`, request);
      return res.data;
    } catch (error) {
      console.error('Failed to update cycle appraisal:', error);
      throw error;
    }
  },

  deleteAppraisal: async (cycleId: number, appraisalId: number): Promise<void> => {
    try {
      await apiClient.delete(`/appraisal-cycles/${cycleId}/appraisals/${appraisalId}`);
    } catch (error) {
      console.error('Failed to delete cycle appraisal:', error);
      throw error;
    }
  },

  bulkDeleteAppraisals: async (cycleId: number, appraisalIds: number[]): Promise<BulkActionResult> => {
    try {
      const res = await apiClient.post(`/appraisal-cycles/${cycleId}/appraisals/bulk-delete`, { appraisalIds });
      return res.data;
    } catch (error) {
      console.error('Failed to bulk delete cycle appraisals:', error);
      throw error;
    }
  },

  exportAppraisals: async (
    cycleId: number,
    appraisalIds?: number[],
    status?: string
  ): Promise<Blob> => {
    try {
      const res = await apiClient.post(
        `/appraisal-cycles/${cycleId}/appraisals/export`,
        { appraisalIds, status },
        { responseType: 'blob' }
      );
      return res.data;
    } catch (error) {
      console.error('Failed to export cycle appraisals:', error);
      throw error;
    }
  },

  sendAppraiseeReminder: async (cycleId: number, appraisalId: number): Promise<void> => {
    try {
      await apiClient.post(`/appraisal-cycles/${cycleId}/appraisals/${appraisalId}/send-appraisee-reminder`);
    } catch (error) {
      console.error('Failed to send appraisee reminder:', error);
      throw error;
    }
  },

  sendCollatorReminder: async (cycleId: number, appraisalId: number): Promise<void> => {
    try {
      await apiClient.post(`/appraisal-cycles/${cycleId}/appraisals/${appraisalId}/send-collator-reminder`);
    } catch (error) {
      console.error('Failed to send collator reminder:', error);
      throw error;
    }
  },

  exportAppraiseeList: async (): Promise<Blob> => {
    try {
      const res = await apiClient.post(
        '/appraisal-cycles/export-appraisee-list',
        null,
        { responseType: 'blob' }
      );
      return res.data;
    } catch (error) {
      console.error('Failed to export appraisee list:', error);
      throw error;
    }
  },
};
